<template>
  <div class="main-container px-5 py-5 xl:px-16">


      <h2 class="text-lg font-semibold">Brand Extractor</h2>

      <div class="flex flex-col sm:flex-row gap-2">
        <InputText
          v-model="brandUrl"
          placeholder="Paste site URL..."
          class="flex-1"
          @keyup.enter="isValidUrl ? getBrandsFromUrl() : null"
        />

        <Select
          v-model="selectedGeo"
          :options="geoOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select GEO"
          class="w-full sm:w-48"
        />

        <Button
          label="Fetch"
          severity="primary"
          :loading="isLoading"
          @click="getBrandsFromUrl"
          :disabled="!isValidUrl || !selectedGeo || isLoading || brandExtractorEnabled == 'false'"
        />
      </div>

      <div v-if="error" class="text-red-500 text-sm">{{ error }}</div>

      <div class="tables-grid mt-5">
        <div  class="brands-table-wrapper">
            <div v-if="isLoading" class="flex flex-col items-center justify-center w-full py-10">
              <i class="pi pi-spin pi-spinner text-5xl mb-4" style="color: var(--color-primary)" aria-label="Loading"></i>
              <span class="text-lg font-semibold text-white">Reading brands from site…</span>
            </div>
            <h3 v-if="brands.length" class="text-md font-semibold mb-2">Brands on the site</h3>
            <DataTable v-if="brands.length" :value="brands">
              <Column field="position" header="#" :headerStyle="headerStyles"/>
              <Column field="brand_name" header="Brand" :headerStyle="headerStyles"/>
            </DataTable>
        </div>

        <div class="brands-table-wrapper">
            <div v-if="suggestedBrandsLoading" class="flex flex-col items-center justify-center w-full py-10">
              <i class="pi pi-spin pi-spinner text-5xl mb-4" style="color: var(--color-primary)" aria-label="Loading"></i>
              <span class="text-lg font-semibold text-white">Checking for possible brands…</span>
            </div>
            <h3 v-if="suggestedBrands.length" class="text-md font-semibold mb-2">
                Possible Brands
            </h3>
            <DataTable v-if="suggestedBrands.length" :value="suggestedBrands">
             
              <Column field="name" header="Name" :headerStyle="headerStyles"/> 
              <Column field="timeAgo" header="Added At" :headerStyle="headerStyles"/>
            </DataTable>
        </div>
      </div>

      <div v-if="!isLoading && !brands.length && hasFetched" class="text-sm text-gray-500">
        No brands found for this URL.
      </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Select from 'primevue/select';
import ToolsService from '@/services/ToolsService';
import { marketOptions } from '@/utils/markets';
import {headerStyles} from '@/global/headerStyles';

const brandExtractorEnabled = process.env.VUE_ENABLE_BRAND_EXTRACTOR == 'true';

const brandUrl = ref('');
const selectedGeo = ref(null);
const geoOptions = marketOptions;
const brands = ref([]);
const isLoading = ref(false);
const error = ref(null);
const hasFetched = ref(false);

const urlPattern = /^(https?:\/\/)?([^\s.]+\.)+[^\s]{2,}$/i;
const isValidUrl = computed(() => urlPattern.test(brandUrl.value.trim()));

const getBrandsFromUrl = async () => {
  if (!brandUrl.value.trim() || !selectedGeo.value) return;
  isLoading.value = true;
  error.value = null;
  brands.value = [];
  suggestedBrands.value = [];

  try {
    const { data } = await ToolsService.getBrandsFromUrl(
      brandUrl.value.trim(),
      selectedGeo.value
    );
    getSuggestedBrands(data);
    brands.value = data;
    hasFetched.value = true;
  } catch (err) {
    error.value = err?.response?.data?.message || 'Failed to fetch brands';
    brands.value = [];
    hasFetched.value = false;
  } finally {
    isLoading.value = false;
  }
};

const suggestedBrands = ref([]);    
const suggestedBrandsLoading = ref(false);

const getSuggestedBrands = async (brandsFromToplist) => {
    try{
        suggestedBrandsLoading.value = true;
        const { data } = await ToolsService.getSuggestedBrands(
            brandsFromToplist,
            selectedGeo.value
        );
        suggestedBrands.value = data;
        console.log('suggestedBrands', suggestedBrands.value);
    }catch(err){
        error.value = err?.response?.data?.message || 'Failed to fetch suggested brands';
        suggestedBrands.value = [];
    }finally{
        suggestedBrandsLoading.value = false;
    }
}
</script>

<style scoped>
.tables-grid {
  display: grid;
  gap: 1.5rem;
}

@media (min-width: 1024px) {
  .tables-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.brands-table-wrapper {
  width: 100%;
}

@media (min-width: 1024px) {
  .brands-table-wrapper {
    max-width: 30vw;
  }
}
</style>
